import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/authContext";
import { FetchContext } from "../context/FetchContext";
import { Row, Col, Button, Spinner, Card, Tabs, Tab } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Invoice from "../components/Invoice2";
import AlertMsg from "../components/PopUp";

// Billing Info is free-form FM text: a run of "- " bullet lines, then plain notes paragraphs,
// separated by blank lines. Render bullet blocks as a real list and the rest as paragraphs.
// FileMaker's own line-break character is \r, not \n, and the Data API doesn't always
// normalize it -- so normalize both before splitting.
function renderBillingInfo(text) {
    const normalized = text.replace(/\r\n?/g, '\n');
    const blocks = normalized.trim().split(/\n\s*\n/);
    return blocks.map((block, i) => {
        const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
        const isList = lines.length > 0 && lines.every(l => l.startsWith('- '));
        if (isList) {
            return (
                <ul key={i} className="mb-3 ps-3">
                    {lines.map((line, j) => <li key={j}>{line.replace(/^- /, '')}</li>)}
                </ul>
            );
        }
        return <p key={i} className="mb-3">{lines.join(' ')}</p>;
    });
}

// FileMaker field names on the Web_Petitions layout, one per box in the petition form.
// If a box shows up blank, the name here doesn't match the layout -- fix it in this map.
const PF = {
    region: "Region", type: "Type", docket: "Docket Number", naics: "NAICS Code", healthcare: "Healthcare",
    union: "Union Name", union2: "Union Local", organizer: "Organizer", organizer2: "Organizer Title",
    winner: "Winner Calc", reasonClosed: "Reason Closed", location: "Location CSZ",
    votes: "Eligible Voters", no: "Against", yes: "For", change: "LRI_ChangeRecord",
    int: "For Intervenor", oth: "For Other", chal: "Challenged Ballots",
    unit: "Unit Included"
};

// Layout rules for the petition form. Desktop mirrors the FileMaker form (label left, boxes
// in a row); under 576px every label sits above its box and fields go full width.
const petitionCss = `
.pet-box { border: 1px solid #bbb; border-radius: 4px; padding: 2px 8px; min-height: 30px; background: #fff; display: inline-block; min-width: 36px; overflow-wrap: anywhere; }
.pet-label { color: #8a8a8a; font-size: 1.05rem; }
.pet-line { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
.pet-line > .pet-label:first-child { width: 110px; }
.pet-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
.pet-stat { display: flex; align-items: center; gap: 8px; }
.pet-stat strong { font-weight: 700; }
.pet-check { width: 22px; height: 22px; min-height: 22px; min-width: 22px; padding: 0; text-align: center; line-height: 20px; }
@media (max-width: 575px) {
  .pet-line > .pet-label:first-child { width: 100%; font-size: .85rem; text-transform: uppercase; margin-bottom: -4px; }
  .pet-line > .pet-box { flex: 1 1 100%; }
  .pet-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; width: 100%; }
  .pet-stat { flex-direction: column; align-items: flex-start; gap: 2px; }
  .pet-stat .pet-box { width: 100%; }
  .pet-docket-row { width: 100%; }
}
`;

// FM checkbox fields come back as their value text (or 1), empty when unchecked.
const isChecked = (v) => v !== "" && v != null && v !== 0 && v !== "0";

function Box({ children, className = "", style }) {
    return <span className={`pet-box ${className}`} style={style}>{children}</span>;
}

function CheckBox({ checked }) {
    return <span className="pet-box pet-check">{checked ? "✓" : ""}</span>;
}

function Line({ label, children }) {
    return (
        <div className="pet-line">
            <span className="pet-label">{label}</span>
            {children}
        </div>
    );
}

function Stat({ label, children }) {
    return <div className="pet-stat"><strong>{label}</strong>{children}</div>;
}

// One petition record laid out like the FileMaker form: Docket line, Union, Organizer,
// Winner / Reason Closed, Location, Election Results, Unit.
function PetitionDetails({ rec }) {
    const v = (key) => rec[PF[key]] ?? "";
    return (
        <div className="mb-4">
            <style>{petitionCss}</style>
            <Line label="Docket">
                <Box>{v("region")}</Box>
                <Box>{v("type")}</Box>
                <Box>{v("docket")}</Box>
                <span className="pet-label ms-2">NAICS</span>
                <Box>{v("naics")}</Box>
                <span className="pet-label ms-2">Healthcare</span>
                <CheckBox checked={isChecked(v("healthcare"))} />
            </Line>
            <Line label="Union">
                <Box style={{ flex: "1 1 240px" }}>{v("union")}</Box>
                <Box style={{ flex: "1 1 120px" }}>{v("union2")}</Box>
            </Line>
            <Line label="Organizer">
                <Box style={{ flex: "1 1 180px" }}>{v("organizer")}</Box>
                <Box style={{ flex: "1 1 180px" }}>{v("organizer2")}</Box>
            </Line>
            <Line label="Winner">
                <Box style={{ minWidth: 140 }}>{v("winner")}</Box>
            </Line>
            <Line label="Reason Closed">
                <Box style={{ flex: "1 1 240px" }}>{v("reasonClosed")}</Box>
            </Line>
            <Line label="Location">
                <Box style={{ flex: "1 1 240px" }}>{v("location")}</Box>
            </Line>
            <Line label="Election Results">
                <div className="pet-stats">
                    <Stat label="Votes"><Box style={{ minWidth: 60 }}>{v("votes")}</Box></Stat>
                    <Stat label="No"><Box style={{ minWidth: 60 }}>{v("no")}</Box></Stat>
                    <Stat label="Yes"><Box style={{ minWidth: 60 }}>{v("yes")}</Box></Stat>
                    <Stat label="Change"><CheckBox checked={isChecked(v("change"))} /></Stat>
                    <Stat label="Int"><Box style={{ minWidth: 60 }}>{v("int")}</Box></Stat>
                    <Stat label="Oth"><Box style={{ minWidth: 60 }}>{v("oth")}</Box></Stat>
                    <Stat label="Chal"><Box style={{ minWidth: 60 }}>{v("chal")}</Box></Stat>
                </div>
            </Line>
            <Line label="Unit">
                <Box style={{ flex: "1 1 300px", minHeight: 90, whiteSpace: "pre-wrap" }}>{String(v("unit")).replace(/\r/g, "\n")}</Box>
            </Line>
        </div>
    );
}

const Project = () => {
    const { authState } = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const [isLoading, setIsLoading] = useState(false);
    const [projObject, setProjObject] = useState({});
    const [descripts, setDescripts] = useState([]);
    const [fixedFeeContracts, setFixedFeeContracts] = useState([]);
    const [petitions, setPetitions] = useState([]); // [{ docket, records: [fieldData] }] per docket
    const [lastAction, setLastAction] = useState(""); // "draft" or "submit"
    const [invoiceShow, setInvoiceShow] = useState(false);
    const [saved, setSaved] = useState(false);
    const nav = useNavigate();
    const { state } = useLocation();

    useEffect(() => {
        initializeProject();
    }, []);

    // Function to initialize the project
    const initializeProject = () => {
        // console.log("Initializing project...");
        if (state?.proj3) {
            // console.log("State passed:", state.proj3);
            setProject(state.proj3);
        } else {
            // console.log("No state passed. Checking local storage...");
            const storedProject = localStorage.getItem("proj3");
            // console.log("Stored project data:", JSON.parse(storedProject));
            if (storedProject) {
                setProject(JSON.parse(storedProject));
            } else {
                console.error("No project data found. Redirecting to Projects page.");
                nav("/"); // Redirect to Projects page if no data is available
            }
        }
    };

    const setProject = (project) => {
        setProjObject(project);
        fetchDescriptions(project);
        fetchFixedFeeContracts();
        fetchPetitions(project);
    };

    async function fetchPetitions(project) {
        const pairs = [
            [project["Region"], project["Docket Number(1)"]],
            [project["Region2"], project["Docket Number2"]],
            [project["Region3"], project["Docket Number3"]]
        ];
        // "region:docket", skipping any slot with no docket
        const dockets = pairs.filter(([, d]) => d).map(([r, d]) => `${r}:${d}`);
        if (dockets.length === 0) return;
        try {
            const response = await fetchContext.authAxios.get("/petitions", {
                params: { dockets: dockets.join(",") }
            });
            setPetitions(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching petitions:", error);
        }
    }

    async function fetchFixedFeeContracts() {
        try {
            const response = await fetchContext.authAxios.get("/fixedfeecontracts");
            setFixedFeeContracts(response.data);
        } catch (error) {
            console.error("Error fetching fixed fee contracts:", error);
        }
    }

    async function fetchDescriptions(project) {
        try {
            await fetchContext.authAxios.get("/fmdescriptions").then((response) => {
                let data = response.data;

                // Get bill types from project's rateInfo object
                let rateTypes = [];
                if (project.rateInfo) {
                    if (project.rateInfo.billType) rateTypes.push(project.rateInfo.billType);
                    if (project.rateInfo.billType2) rateTypes.push(project.rateInfo.billType2);
                }

                // Only filter Consultant - Daily and Consultant - Hourly
                data = data.filter(desc => {
                    if (
                        desc.text === "Consultant - Daily" ||
                        desc.text === "Consultant - Hourly"
                    ) {
                        return rateTypes.includes(desc.text);
                    }
                    return true; // keep all other descriptions
                });

                setDescripts(data);
                return data;
            });
        } catch (error) {
            console.error("Error fetching descriptions:", error);
            return [];
        }
    }



    // FM returns the field as "AIProhibited" on the flattened project record
    const aiProhibited = String(projObject.AIProhibited ?? projObject["Projects::AIProhibited"] ?? "")
        .trim().toLowerCase() === "yes";

    return (
        <>
            {invoiceShow ? (
                <Row className="justify-content-center app">
                    <h5 className="text-center">Invoice Creation</h5>
                    <div>
                        <Invoice
                            descripts={descripts}
                            fixedFeeContracts={fixedFeeContracts}
                            data={projObject}
                            btnSelect={true}
                            invShow={() => {
                                setInvoiceShow(false);
                            }}
                            setSaved={() => setSaved(true)}
                            setLoad={() => setIsLoading(true)}
                            setInvShow={() => setInvoiceShow(false)}
                            setLastAction={(action) => { setLastAction(action); console.log("Last action set to:", action); }}
                        />
                    </div>
                    <Button
                        style={{ width: "150px" }}
                        className="btn text-white my-5 btn-danger mobbtn"
                        onClick={() => setInvoiceShow(false)}
                    >
                        Cancel
                    </Button>
                </Row>
            ) : (
                <>
                    {saved ? (
                        <AlertMsg
                            title={lastAction === "submit" ? "Submitted!" : "Draft Saved!"}
                            vari={lastAction === "submit" ? "primary" : "success"}
                            message={
                                lastAction === "submit"
                                    ? "Your invoice was submitted successfully. You can review it on the Invoices page. Edits are locked."
                                    : "Your invoice draft was saved. You can modify it on the Invoices page."
                            }
                            showButton="true"
                            link="Invoices"
                            setLoad={() => setIsLoading(false)}
                            setCancel={() => setSaved(false)}
                        />
                    ) : (
                        <Row className="justify-content-center">
                            {isLoading ? (
                                <>
                                    <p className="pl-3 text-center">Loading...</p>
                                    <Spinner animation="border" variant="danger" />
                                </>
                            ) : (
                                <>
                                    <h4 className="text-center">{projObject.ProjectName}</h4>
                                    <Button
                                        style={{ width: "180px" }}
                                        className="btn text-white text-center my-3 mobbtn"
                                        onClick={() => setInvoiceShow(true)}
                                    >
                                        Create Invoice
                                    </Button>
                                    <Card className="mx-auto shadow-sm project-details-card mb-5">
                                        <Card.Body>
                                            <Row className="gy-3">
                                                {projObject.Company_Name ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Company Name</div>
                                                        <div>{projObject.Company_Name}</div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Title ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Title</div>
                                                        <div>{projObject.Title}</div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Date ? (
                                                    <Col xs={12}>
                                                        <div className="text-muted small text-uppercase">Statement Of Work</div>
                                                        <div>{projObject.Date}</div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Name ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Name</div>
                                                        <div>{projObject.Name}</div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Address ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Address</div>
                                                        <div>
                                                            <a
                                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                                    `${projObject.Address} ${projObject.City || ''} ${projObject.State || ''} ${projObject.Zip || ''}`
                                                                )}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {projObject.Address}
                                                                {(projObject.City || projObject.State || projObject.Zip) ? (
                                                                    <>, {projObject.City} {projObject.State} {projObject.Zip}</>
                                                                ) : null}
                                                            </a>
                                                        </div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Email ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Email</div>
                                                        <div><a href={`mailto:${projObject.Email}`}>{projObject.Email}</a></div>
                                                    </Col>
                                                ) : null}
                                                {projObject.Phone ? (
                                                    <Col xs={12} md={6}>
                                                        <div className="text-muted small text-uppercase">Phone</div>
                                                        <div><a href={`tel:${projObject.Phone}`}>{projObject.Phone}</a></div>
                                                    </Col>
                                                ) : null}
                                            </Row>

                                            {projObject.BillingInfo || aiProhibited ? (
                                                <div className="mt-4 pt-3 border-top text-start">
                                                    {aiProhibited ? (
                                                        <div className="fw-bold mb-3" style={{ color: "red" }}>AI Prohibited</div>
                                                    ) : null}
                                                    {projObject.BillingInfo ? (
                                                        <>
                                                            <div className="text-muted small text-uppercase mb-2">Billing Info</div>
                                                            {renderBillingInfo(projObject.BillingInfo)}
                                                        </>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            {petitions.some(p => p.records.length > 0) ? (
                                                <Tabs defaultActiveKey="0" className="mt-4" id="docket-tabs">
                                                    {petitions.map((p, i) => (
                                                        <Tab key={i} eventKey={String(i)} title={`Docket ${i + 1}`}>
                                                            <div className="pt-3 text-start">
                                                                {p.records.length === 0 ? (
                                                                    <div className="text-muted">No petition found for this docket.</div>
                                                                ) : p.records.map((rec, j) => (
                                                                    <PetitionDetails key={j} rec={rec} />
                                                                ))}
                                                            </div>
                                                        </Tab>
                                                    ))}
                                                </Tabs>
                                            ) : null}
                                        </Card.Body>
                                    </Card>
                                </>
                            )}
                        </Row>
                    )}
                </>
            )}
        </>
    );
};

export default Project;